import { ProjectId } from "@custom-types/base-types";
import { ProjectArchivingState, SdbProject } from "@custom-types/project-types";
import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { removeMemberFromCompany } from "@store/members/members-slice-thunk";
import {
  deleteProject,
  deleteProjectSnapshot,
  editProjectFeature,
  fetchProjectContext,
  fetchProjectDetails,
  fetchProjects,
  fetchProjectsByIds,
  fetchProjectSettings,
  fetchProjectSnapshots,
  fetchSnapshotMembers,
  fetchSnapshotsBeingCreatedCount,
  inviteMemberToSnapshot,
  removeMemberFromProject,
  removeMemberFromSnapshot,
  updateMemberRoleInProjectBulk,
  updateMemberRoleInProjectSingle,
  updateProjectDetails,
  updateProjectSettings,
  updateSnapshotDetails,
} from "@store/projects/projects-slice-thunk";
import {
  ProjectsState,
  RemoveMemberResult,
  UpdateMemberRoleInProject,
} from "@store/projects/projects-slice-types";
import {
  convertProjectsResponseToSdbProjects,
  convertProjectTypesToSdbProject,
  getListType,
  PROJECTS_LIST_INITIAL_STATE,
  restoreMissingProjectFields,
} from "@store/projects/projects-slice-utils";
import { BaseEntityAdapter } from "@store/store-types";
import { getProjectArchivingState } from "@utils/project-utils";

/** Creates an entity adapter to store a map with all the projects that the user has access to. */
export const projectsAdapter: BaseEntityAdapter<SdbProject> =
  createEntityAdapter({
    selectId: (project) => project.id,
  });

const initialState: ProjectsState = {
  ...projectsAdapter.getInitialState(),

  lists: {
    active: PROJECTS_LIST_INITIAL_STATE,
    archived: PROJECTS_LIST_INITIAL_STATE,
    transient: PROJECTS_LIST_INITIAL_STATE,
  },

  selectedProject: {
    id: null,
    context: null,
    settings: null,
    selectedSnapshotId: null,
    isSnapshotSidePanelOpen: false,
    selectedSnapshotMembers: [],
    snapshotsBeingCreatedCount: 0,
  },

  fetching: {
    isFetchingProjects: false,
    isUpdatingProjects: false,
    isRemovingProjectMember: false,
    isFetchingProjectContext: false,
    isFetchingProjectSettings: false,
    isUpdatingProjectSettings: false,
    isFetchingProjectSnapshots: false,
    isFetchingSnapshotMembers: "uninitialized",
    isAddingSnapshotMember: false,
    isRemovingSnapshotMember: false,
    updatingSnapshotDetails: "uninitialized",
    processingProjects: {},
  },
  deletedProjects: {},
};

/**
 * Removes locally a member from a project, by removing it from members array
 * attribute of the project.
 */
function removeLocalMemberFromProject(
  state: ProjectsState,
  result: RemoveMemberResult
): void {
  const project = projectsAdapter
    .getSelectors()
    .selectById(state, result.projectId);
  if (project) {
    // If the projects is in the store update the members attribute
    // by removing the member that was removed from it.
    projectsAdapter.updateOne(state, {
      id: project.id,
      changes: {
        members: project.members.filter(
          (member) => member.identity !== result.member.identity
        ),
      },
    });
  }
  state.fetching.isRemovingProjectMember = false;
}

/**
 * Update a member role in project store by updating it from members array attribute of the project.
 */
function updateMemberRoleInProjectStore(
  state: ProjectsState,
  result: UpdateMemberRoleInProject
): void {
  const project = projectsAdapter
    .getSelectors()
    .selectById(state, result.projectId);

  // If the projects is in the store update the member role
  if (project) {
    projectsAdapter.updateOne(state, {
      id: project.id,
      changes: {
        members: project.members.map((member) => {
          if (member.identity === result.userIdentity) {
            return { ...member, role: result.role };
          }
          return member;
        }),
      },
    });
  }
}

/** Removes the project ID from all projects lists and adjusts totalCount */
function removeProjectIdFromLists(
  state: ProjectsState,
  projectId: ProjectId
): void {
  Object.values(state.lists).forEach((list) => {
    const idx = list.ids.indexOf(projectId);
    if (idx !== -1) {
      list.ids.splice(idx, 1);
      list.totalCount--;
    }
  });
}

/** Moves a project ID between active and archived lists and adjusts totalCount */
function moveProjectIdToList(
  state: ProjectsState,
  projectId: ProjectId,
  to: ProjectArchivingState
): void {
  removeProjectIdFromLists(state, projectId);
  state.lists[to].ids.unshift(projectId);
  state.lists[to].totalCount++;
}

/** Slice to access state of loaded projects */
const projectsSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    /** Accepts a single project entity and adds or replaces it. */
    setOneProjects: projectsAdapter.setOne,

    /** Adds a list of project IDs to the map of processing projects */
    addProcessingProjects(state, action: PayloadAction<ProjectId[]>) {
      action.payload.forEach(
        (projectId) => (state.fetching.processingProjects[projectId] = true)
      );
    },

    /** Stores the project id of the selected project. */
    setSelectedProjectId(state, action: PayloadAction<string | null>) {
      state.selectedProject.id = action.payload;
    },

    /** Resets the selected project state */
    resetSelectedProject(state) {
      state.selectedProject.id = null;
      state.selectedProject.context = null;
      state.selectedProject.settings = null;
      state.selectedProject.selectedSnapshotId = null;
      state.selectedProject.isSnapshotSidePanelOpen = false;
      state.selectedProject.selectedSnapshotMembers = [];
      state.selectedProject.snapshotsBeingCreatedCount = 0;
    },

    /** Sets the selected snapshot ID and opens the side panel */
    setSelectedSnapshotId(state, action: PayloadAction<string | null>) {
      state.selectedProject.selectedSnapshotId = action.payload;
      state.selectedProject.isSnapshotSidePanelOpen = action.payload !== null;
    },

    /** Sets whether the snapshot side panel is open */
    setSnapshotSidePanelOpen(state, action: PayloadAction<boolean>) {
      state.selectedProject.isSnapshotSidePanelOpen = action.payload;
      if (!action.payload) {
        state.selectedProject.selectedSnapshotId = null;
      }
    },

    /** Removes a list of project IDs from the map of processing projects */
    removeProcessingProjects(state, action: PayloadAction<ProjectId[]>) {
      action.payload.forEach(
        (projectId) => delete state.fetching.processingProjects[projectId]
      );
    },

    /** Resets the deleted projects state */
    resetDeletedProjects(state) {
      state.deletedProjects = {};
    },

    /** Set the fetching of updating projects */
    setFetchingUpdatingProjects(state, action: PayloadAction<boolean>) {
      state.fetching.isUpdatingProjects = action.payload;
    },

    /** Adds a project to the state and updates the respective projects list */
    addProject(state, action: PayloadAction<SdbProject>) {
      projectsAdapter.setOne(state, action.payload);
      const listType = getProjectArchivingState(action.payload.archivingState);
      state.lists[listType].ids.unshift(action.payload.id);
      state.lists[listType].totalCount++;
    },

    /** Resets the projects state */
    resetProjectsState: () => initialState,
  },

  extraReducers(builder) {
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.fetching.isFetchingProjects = true;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        const sdbProjects = convertProjectsResponseToSdbProjects(
          action.payload.data
        );
        projectsAdapter.upsertMany(state, sdbProjects);
        const listType = getListType(action.meta.arg);
        const list = state.lists[listType];

        // If there is no next cursor in the request, it means it's the first page
        // and we need to replace the current IDs with the new ones.
        // If there is a next cursor, we need to append the new IDs to the existing ones
        // but only if they are not already present in the list (to avoid duplicates).
        const currentNextPageCursor = action.meta.arg.next;
        const newIds = sdbProjects.map((project) => project.id);
        if (!currentNextPageCursor) {
          list.ids = newIds;
        } else {
          const newNonDuplicateIds = newIds.filter(
            (id) => !list.ids.includes(id)
          );
          list.ids.push(...newNonDuplicateIds);
        }

        list.nextPageCursor = action.payload.page.next;
        list.totalCount = action.payload.page.total;
        list.hasFetchedFirstPage = true;
        state.fetching.isFetchingProjects = false;
      })
      .addCase(fetchProjects.rejected, (state) => {
        state.fetching.isFetchingProjects = false;
      })

      .addCase(fetchProjectDetails.pending, (state) => {
        state.fetching.isFetchingProjects = true;
      })
      .addCase(fetchProjectDetails.fulfilled, (state, action) => {
        state.fetching.isFetchingProjects = false;
        state.selectedProject.id = action.payload.id;
        projectsAdapter.setOne(
          state,
          convertProjectTypesToSdbProject(action.payload)
        );
      })
      .addCase(fetchProjectDetails.rejected, (state) => {
        state.fetching.isFetchingProjects = false;
      })

      .addCase(updateProjectDetails.pending, (state) => {
        state.fetching.isUpdatingProjects = true;
      })
      .addCase(updateProjectDetails.fulfilled, (state, action) => {
        state.fetching.isUpdatingProjects = false;

        /**
         * Get the existing project from Redux store to restore missing fields.
         * The updateProjectDetails API only returns basic ProjectDetails without some fields
         * (like featuresAvailable) that are needed for UI functionality. e.g., to control
         * which bulk actions (archive/unarchive) should be available in the UI.
         */
        const existingProject = projectsAdapter
          .getSelectors()
          .selectById(state, action.payload.id);

        // We can update in the map view. We need to check if the project exists in the projects slice first to avoid an error.
        if (!existingProject) {
          return;
        }

        const updatedProject = convertProjectTypesToSdbProject(action.payload);
        const restoredProject = restoreMissingProjectFields({
          updatedProject,
          existingProject,
        });
        projectsAdapter.setOne(state, restoredProject);

        // If the project update included an archiving state change, update the projects lists state
        const previousState = getProjectArchivingState(
          existingProject.archivingState
        );
        const newState = getProjectArchivingState(
          restoredProject.archivingState
        );
        if (previousState !== newState) {
          moveProjectIdToList(state, restoredProject.id, newState);
        }
      })
      .addCase(updateProjectDetails.rejected, (state) => {
        state.fetching.isUpdatingProjects = false;
      })

      .addCase(removeMemberFromProject.pending, (state) => {
        state.fetching.isRemovingProjectMember = true;
      })
      .addCase(removeMemberFromProject.fulfilled, (state, action) => {
        removeLocalMemberFromProject(state, action.payload);
      })
      .addCase(removeMemberFromProject.rejected, (state) => {
        state.fetching.isRemovingProjectMember = false;
      })

      .addCase(removeMemberFromCompany.fulfilled, (state, action) => {
        // When a member is removed from the workspace, remove them from the currently
        // selected project and snapshot so the UI updates immediately. Other projects
        // get fresh data from fetchProjectDetails when the user navigates to them.
        const selectedProjectId = state.selectedProject.id;
        if (selectedProjectId) {
          removeLocalMemberFromProject(state, {
            projectId: selectedProjectId,
            member: action.payload,
          });

          // Remove from snapshot members as well
          state.selectedProject.selectedSnapshotMembers =
            state.selectedProject.selectedSnapshotMembers.filter(
              (member) => member.identity !== action.payload.identity
            );
        }
      })

      .addCase(updateMemberRoleInProjectSingle.pending, (state) => {
        state.fetching.isUpdatingProjects = true;
      })
      .addCase(updateMemberRoleInProjectSingle.fulfilled, (state, action) => {
        updateMemberRoleInProjectStore(state, action.payload);
        state.fetching.isUpdatingProjects = false;
      })
      .addCase(updateMemberRoleInProjectSingle.rejected, (state) => {
        state.fetching.isUpdatingProjects = false;
      })

      .addCase(updateMemberRoleInProjectBulk.pending, (state) => {
        state.fetching.isUpdatingProjects = true;
      })
      .addCase(updateMemberRoleInProjectBulk.fulfilled, (state, action) => {
        updateMemberRoleInProjectStore(state, action.payload);
        state.fetching.isUpdatingProjects = false;
      })
      .addCase(updateMemberRoleInProjectBulk.rejected, (state) => {
        state.fetching.isUpdatingProjects = false;
      })

      .addCase(fetchProjectContext.pending, (state) => {
        state.fetching.isFetchingProjectContext = true;
      })
      .addCase(fetchProjectContext.fulfilled, (state, action) => {
        state.selectedProject.context = action.payload;
        state.fetching.isFetchingProjectContext = false;
      })
      .addCase(fetchProjectContext.rejected, (state) => {
        state.fetching.isFetchingProjectContext = false;
      })

      .addCase(editProjectFeature.fulfilled, (state, action) => {
        projectsAdapter.updateOne(state, {
          id: action.payload.selectedProject.id,
          changes: {
            features: {
              ...action.payload.selectedProject.features,
              ...action.payload.newFeatures,
            },
          },
        });
      })

      .addCase(deleteProject.pending, (state, action) => {
        state.fetching.processingProjects[action.meta.arg.projectId] = true;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        const projectId = action.payload;
        projectsAdapter.removeOne(state, projectId);
        removeProjectIdFromLists(state, projectId);
        state.deletedProjects[projectId] = true;
        delete state.fetching.processingProjects[projectId];
      })
      .addCase(deleteProject.rejected, (state, action) => {
        delete state.fetching.processingProjects[action.meta.arg.projectId];
      })

      .addCase(fetchProjectSettings.pending, (state) => {
        state.fetching.isFetchingProjectSettings = true;
      })
      .addCase(fetchProjectSettings.fulfilled, (state, action) => {
        state.selectedProject.settings = action.payload;
        state.fetching.isFetchingProjectSettings = false;
      })
      .addCase(fetchProjectSettings.rejected, (state) => {
        state.fetching.isFetchingProjectSettings = false;
      })

      .addCase(updateProjectSettings.pending, (state) => {
        state.fetching.isUpdatingProjectSettings = true;
      })
      .addCase(updateProjectSettings.fulfilled, (state, action) => {
        state.selectedProject.settings = action.payload;
        state.fetching.isUpdatingProjectSettings = false;
      })
      .addCase(updateProjectSettings.rejected, (state) => {
        state.fetching.isUpdatingProjectSettings = false;
      })

      .addCase(fetchProjectSnapshots.pending, (state) => {
        state.fetching.isFetchingProjectSnapshots = true;
      })
      .addCase(fetchProjectSnapshots.fulfilled, (state, action) => {
        projectsAdapter.updateOne(state, {
          id: action.payload.projectId,
          changes: { snapshots: action.payload.snapshots },
        });
        state.fetching.isFetchingProjectSnapshots = false;
      })
      .addCase(fetchProjectSnapshots.rejected, (state) => {
        state.fetching.isFetchingProjectSnapshots = false;
      })

      .addCase(fetchSnapshotsBeingCreatedCount.fulfilled, (state, action) => {
        state.selectedProject.snapshotsBeingCreatedCount = action.payload;
      })

      .addCase(deleteProjectSnapshot.fulfilled, (state, action) => {
        const { projectId, snapshotId } = action.payload;
        const project = projectsAdapter
          .getSelectors()
          .selectById(state, projectId);
        if (project) {
          projectsAdapter.updateOne(state, {
            id: projectId,
            changes: {
              snapshots: project.snapshots.filter(
                (snapshot) => snapshot.id !== snapshotId
              ),
            },
          });
        }
      })

      .addCase(fetchSnapshotMembers.pending, (state) => {
        state.fetching.isFetchingSnapshotMembers = "pending";
        // Clear stale members immediately so the previous snapshot's list
        // is not shown while the new snapshot's members are loading.
        state.selectedProject.selectedSnapshotMembers = [];
      })
      .addCase(fetchSnapshotMembers.fulfilled, (state, action) => {
        state.selectedProject.selectedSnapshotMembers =
          action.payload.members ?? [];
        state.fetching.isFetchingSnapshotMembers = "succeeded";
      })
      .addCase(fetchSnapshotMembers.rejected, (state) => {
        state.fetching.isFetchingSnapshotMembers = "rejected";
      })

      .addCase(inviteMemberToSnapshot.pending, (state) => {
        state.fetching.isAddingSnapshotMember = true;
      })
      .addCase(inviteMemberToSnapshot.fulfilled, (state, action) => {
        state.selectedProject.selectedSnapshotMembers =
          action.payload.data.members;
        state.fetching.isAddingSnapshotMember = false;
      })
      .addCase(inviteMemberToSnapshot.rejected, (state) => {
        state.fetching.isAddingSnapshotMember = false;
      })

      .addCase(removeMemberFromSnapshot.pending, (state) => {
        state.fetching.isRemovingSnapshotMember = true;
      })
      .addCase(removeMemberFromSnapshot.fulfilled, (state, action) => {
        state.selectedProject.selectedSnapshotMembers =
          state.selectedProject.selectedSnapshotMembers.filter(
            (member) => member.identity !== action.payload.member.identity
          );
        state.fetching.isRemovingSnapshotMember = false;
      })
      .addCase(removeMemberFromSnapshot.rejected, (state) => {
        state.fetching.isRemovingSnapshotMember = false;
      })

      .addCase(fetchProjectsByIds.pending, (state) => {
        state.fetching.isFetchingProjects = true;
      })
      .addCase(fetchProjectsByIds.fulfilled, (state, action) => {
        const sdbProjects = action.payload.map((project) =>
          convertProjectTypesToSdbProject(project)
        );
        projectsAdapter.upsertMany(state, sdbProjects);
        state.fetching.isFetchingProjects = false;
      })
      .addCase(fetchProjectsByIds.rejected, (state) => {
        state.fetching.isFetchingProjects = false;
      })

      .addCase(updateSnapshotDetails.pending, (state) => {
        state.fetching.updatingSnapshotDetails = "pending";
      })
      .addCase(updateSnapshotDetails.fulfilled, (state, action) => {
        state.fetching.updatingSnapshotDetails = "succeeded";

        const selectedProjectId = state.selectedProject.id;

        if (selectedProjectId) {
          const project = projectsAdapter
            .getSelectors()
            .selectById(state, selectedProjectId);

          if (project) {
            const updatedSnapshots = project.snapshots.map((snapshot) =>
              snapshot.id === action.payload.id ? action.payload : snapshot
            );

            projectsAdapter.updateOne(state, {
              id: selectedProjectId,
              changes: { snapshots: updatedSnapshots },
            });
          }
        }
      })
      .addCase(updateSnapshotDetails.rejected, (state) => {
        state.fetching.updatingSnapshotDetails = "rejected";
      });
  },
});

export const {
  setOneProjects,
  addProcessingProjects,
  setSelectedProjectId,
  resetSelectedProject,
  removeProcessingProjects,
  resetProjectsState,
  setFetchingUpdatingProjects,
  addProject,
  resetDeletedProjects,
  setSelectedSnapshotId,
  setSnapshotSidePanelOpen,
} = projectsSlice.actions;

export const projectsReducer = projectsSlice.reducer;
